import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { rhm, apiError, dOnly, titleCase } from "../api";
import { useRhmAuth } from "../RhmAuthContext";
import { Badge, Btn, Card, Empty, Field, Input, Modal, PageHead, Select, Table, Td, statusTone } from "../ui";

export default function RhmBatches() {
    const { can } = useRhmAuth();
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [form, setForm] = useState({ product_id: "", batch_number: "", mfg_date: "", expiry_date: "", quantity: "", warehouse: "Main" });

    const { data: batches = [], isLoading } = useQuery({
        queryKey: ["rhm-all-batches"],
        queryFn: async () => (await rhm.get("/batches")).data,
    });
    const { data: products = [] } = useQuery({
        queryKey: ["rhm-products"],
        queryFn: async () => (await rhm.get("/products")).data,
    });

    const invalidate = () => {
        qc.invalidateQueries({ queryKey: ["rhm-all-batches"] });
        qc.invalidateQueries({ queryKey: ["rhm-inventory"] });
        qc.invalidateQueries({ queryKey: ["rhm-inventory-alerts"] });
    };

    const create = useMutation({
        mutationFn: async (payload) => (await rhm.post("/batches", payload)).data,
        onSuccess: () => { toast.success("Batch created"); setOpen(false); invalidate(); },
        onError: (e) => toast.error(apiError(e)),
    });
    const update = useMutation({
        mutationFn: async ({ id, payload }) => (await rhm.patch(`/batches/${id}`, payload)).data,
        onSuccess: () => { toast.success("Batch updated"); setEdit(null); invalidate(); },
        onError: (e) => toast.error(apiError(e)),
    });
    const scan = useMutation({
        mutationFn: async () => (await rhm.post("/batches/expire-scan")).data,
        onSuccess: (d) => { toast.success(`${d.batches_expired} expired batch(es) processed`); invalidate(); },
        onError: (e) => toast.error(apiError(e)),
    });

    return (
        <div data-testid="rhm-batches-page">
            <PageHead
                title="Batches & Expiry"
                sub="Every unit of stock lives in a batch. Expired batches can never be sold."
                action={
                    <div className="flex gap-3">
                        {can("inventory.approve") && (
                            <Btn variant="ghost" onClick={() => scan.mutate()} data-testid="rhm-expire-scan">RUN EXPIRY SCAN</Btn>
                        )}
                        {can("batches.create") && (
                            <Btn onClick={() => setOpen(true)} data-testid="rhm-add-batch">
                                <Plus className="w-3.5 h-3.5 inline mr-1.5" /> ADD BATCH
                            </Btn>
                        )}
                    </div>
                }
            />

            <Card title={`${batches.length} batch(es)`}>
                {isLoading ? <div className="mono text-xs text-zinc-500">LOADING…</div> : (
                    <Table testId="rhm-batches-table"
                        head={["Batch", "Product", "Mfg", "Expiry", "Days Left", "Qty", "Available", "Status", ""]}
                        empty={!batches.length && <Empty msg="No batches yet." />}>
                        {batches.map((b) => (
                            <tr key={b.id}>
                                <Td className="mono text-xs">{b.batch_number || <span className="text-zinc-600">unnumbered</span>}</Td>
                                <Td className="font-semibold">{b.product_name}</Td>
                                <Td className="text-sm">{b.mfg_date ? dOnly(b.mfg_date) : "—"}</Td>
                                <Td className="text-sm">{b.expiry_date ? dOnly(b.expiry_date) : "—"}</Td>
                                <Td className={b.days_left !== null && b.days_left < 90 ? "text-orange-300" : "text-zinc-400"}>
                                    {b.days_left === null ? "—" : b.days_left}
                                </Td>
                                <Td>{b.quantity}</Td>
                                <Td className="font-bold">{b.available}</Td>
                                <Td><Badge tone={statusTone(b.status)}>{titleCase(b.status)}</Badge></Td>
                                <Td>
                                    {can("batches.edit") && (
                                        <button onClick={() => setEdit(b)} data-testid={`rhm-edit-batch-${b.id}`}
                                            className="mono text-[10px] tracking-[0.16em] text-gold">EDIT</button>
                                    )}
                                </Td>
                            </tr>
                        ))}
                    </Table>
                )}
            </Card>

            <Modal open={open} onClose={() => setOpen(false)} title="Add Batch" testId="rhm-add-batch-modal">
                <form onSubmit={(e) => { e.preventDefault(); create.mutate({ ...form, quantity: Number(form.quantity || 0) }); }} className="space-y-4">
                    <Field label="Product" required>
                        <Select required value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} data-testid="batch-product">
                            <option value="">Select product</option>
                            {products.map((p) => <option key={p.id} value={p.id}>{p.product_code} · {p.name}</option>)}
                        </Select>
                    </Field>
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Batch number" hint="May be left blank until known">
                            <Input value={form.batch_number} onChange={(e) => setForm({ ...form, batch_number: e.target.value })} data-testid="batch-number" />
                        </Field>
                        <Field label="Quantity" required>
                            <Input type="number" min="0" required value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="batch-qty" />
                        </Field>
                        <Field label="Manufacturing date">
                            <Input type="date" value={form.mfg_date} onChange={(e) => setForm({ ...form, mfg_date: e.target.value })} data-testid="batch-mfg" />
                        </Field>
                        <Field label="Expiry date">
                            <Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="batch-expiry" />
                        </Field>
                        <Field label="Warehouse">
                            <Input value={form.warehouse} onChange={(e) => setForm({ ...form, warehouse: e.target.value })} />
                        </Field>
                    </div>
                    <div className="flex gap-3">
                        <Btn type="submit" disabled={create.isPending} data-testid="batch-submit">CREATE BATCH</Btn>
                        <Btn type="button" variant="ghost" onClick={() => setOpen(false)}>CANCEL</Btn>
                    </div>
                </form>
            </Modal>

            <Modal open={!!edit} onClose={() => setEdit(null)} title="Edit Batch" testId="rhm-edit-batch-modal">
                {edit && (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            update.mutate({
                                id: edit.id,
                                payload: {
                                    batch_number: edit.batch_number || null,
                                    mfg_date: edit.mfg_date || null,
                                    expiry_date: edit.expiry_date || null,
                                    warehouse: edit.warehouse,
                                },
                            });
                        }}
                        className="space-y-4"
                    >
                        <div className="grid md:grid-cols-2 gap-4">
                            <Field label="Batch number">
                                <Input value={edit.batch_number || ""} onChange={(e) => setEdit({ ...edit, batch_number: e.target.value })} data-testid="edit-batch-number" />
                            </Field>
                            <Field label="Warehouse">
                                <Input value={edit.warehouse || ""} onChange={(e) => setEdit({ ...edit, warehouse: e.target.value })} />
                            </Field>
                            <Field label="Manufacturing date">
                                <Input type="date" value={(edit.mfg_date || "").slice(0, 10)} onChange={(e) => setEdit({ ...edit, mfg_date: e.target.value })} data-testid="edit-batch-mfg" />
                            </Field>
                            <Field label="Expiry date">
                                <Input type="date" value={(edit.expiry_date || "").slice(0, 10)} onChange={(e) => setEdit({ ...edit, expiry_date: e.target.value })} data-testid="edit-batch-expiry" />
                            </Field>
                        </div>
                        <div className="flex gap-3">
                            <Btn type="submit" disabled={update.isPending} data-testid="edit-batch-submit">SAVE</Btn>
                            <Btn type="button" variant="ghost" onClick={() => setEdit(null)}>CANCEL</Btn>
                        </div>
                    </form>
                )}
            </Modal>
        </div>
    );
}
